import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AvailableLanguages } from '@constants';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { ROLES_KEYS, User } from '@interfaces';
import { UserAuthFacade } from '@state/user-auth';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { RouterFacade } from '@state/router';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.scss'],
})
export class UserSettingsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new ReplaySubject(1);

  public selectedTabIndex = 0;

  // The order of items in `tabKeys` must strictly match the order of the displayed tabs.
  // Any change in tab arrangement or naming must be reflected here to ensure correct tab selection and navigation behavior.
  private readonly tabKeys = ['details', 'transactions', 'consents', 'data-listing-requests'];

  public myself$: Observable<User | null> = this.userAuthFacade.myself$;
  public myself: User;

  public currentLang: AvailableLanguages;

  public showPurchaseList = false;
  public showConsents = false;
  public showDataListingRequests = false;

  protected readonly ROLES_KEYS = ROLES_KEYS;

  constructor(
    private readonly userAuthFacade: UserAuthFacade,
    private readonly translateService: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly routerFacade: RouterFacade
  ) {}

  public ngOnInit() {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const tabParam = params.get('tab');
      this.selectedTabIndex = this.getTabIndexFromParam(tabParam);
    });

    this.currentLang = this.translateService.currentLang as AvailableLanguages;
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
    });

    this.myself$
      .pipe(
        filter(myself => !!myself?.id && !!myself?.email),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(myself => {
        this.myself = myself;

        this.showPurchaseList = myself.role?.key === ROLES_KEYS.Foreigner;
        this.showConsents = myself.role?.key === ROLES_KEYS.Foreigner;
        this.showDataListingRequests = myself.role?.key === ROLES_KEYS.Foreigner;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public onTabChange(index: number): void {
    const tabParam = this.getTabParamFromIndex(index);
    this.routerFacade.changeRoute({
      linkParams: [],
      extras: {
        replaceUrl: true,
        queryParams: { tab: tabParam },
      },
    });
  }

  private getTabIndexFromParam(tabParam: string): number {
    const index = this.tabKeys.indexOf(tabParam);
    return index >= 0 ? index : 0;
  }

  private getTabParamFromIndex(index: number): string {
    return this.tabKeys[index] || this.tabKeys[0];
  }
}
